import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { API_BASE } from '../utils/api';
import { isTokenExpired, shouldRefreshToken } from '../utils/tokenUtils';

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  company_id: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  login: (token: string, refreshToken: string, user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  refreshAccessToken: () => Promise<boolean>;
  getValidToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  const clearStoredAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const checkAndRefreshToken = useCallback(async () => {
    if (!refreshToken) return;
    
    try {
      const base = API_BASE;
      const response = await fetch(`${base}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setToken(data.access_token);
        localStorage.setItem('token', data.access_token);
        if (data.user) {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
      } else {
        // Refresh failed, clear auth
        clearStoredAuth();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearStoredAuth();
    }
  }, [refreshToken]);

  useEffect(() => {
    // Check for existing authentication on app load
    const storedToken = localStorage.getItem('token');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedRefreshToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        
        // Check if token is expired
        if (isTokenExpired(storedToken)) {
          console.log('Token is expired, clearing auth');
          clearStoredAuth();
          return;
        }
        
        setToken(storedToken);
        setRefreshToken(storedRefreshToken);
        setUser(userData);
        
        // Check if token needs refresh (expires within 5 minutes)
        if (shouldRefreshToken(storedToken)) {
          console.log('Token needs refresh, attempting refresh');
          checkAndRefreshToken();
        }
      } catch (error) {
        console.error('Error parsing stored auth data:', error);
        clearStoredAuth();
      }
    }
  }, [checkAndRefreshToken]);

  const login = (newToken: string, newRefreshToken: string, userData: User) => {
    setToken(newToken);
    setRefreshToken(newRefreshToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    clearStoredAuth();
  };

  const refreshAccessToken = async (): Promise<boolean> => {
    if (!refreshToken) return false;
    
    try {
      const response = await fetch(`${API_BASE}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setToken(data.access_token);
        localStorage.setItem('token', data.access_token);
        if (data.user) {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        return true;
      } else {
        clearStoredAuth();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearStoredAuth();
      return false;
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    const role = (user.role || '').toLowerCase();
    // Company-level admins have all permissions (treat 'admin' same as company_admin)
    if (['super_admin', 'company_admin', 'admin'].includes(role)) return true;
    // Basic mapping for common roles
    const readOnly = ['viewer'];
    const limited = ['user', 'facility_manager', 'quality_manager', 'compliance_manager'];
    const isWrite = permission.endsWith(':create') || permission.endsWith(':update') || permission.endsWith(':write') || permission.endsWith(':delete') || permission.includes(':submit') || permission.includes(':approve');
    if (readOnly.includes(role)) return !isWrite;
    if (limited.includes(role)) {
      // Allow reads; restrict user management writes
      if (permission.startsWith('user:') && isWrite) return false;
      return true;
    }
    return false;
  };

  const isAuthenticated = (): boolean => {
    if (!token || !user) return false;
    
    // Check if token is expired
    if (isTokenExpired(token)) {
      console.log('Token is expired, user not authenticated');
      clearStoredAuth();
      return false;
    }
    
    return true;
  };

  const getValidToken = async (): Promise<string | null> => {
    if (!token) return null;
    
    // Check if token is expired
    if (isTokenExpired(token)) {
      console.log('Token is expired, attempting refresh');
      const refreshed = await refreshAccessToken();
      return refreshed ? localStorage.getItem('token') : null;
    }
    
    // Check if token needs refresh soon
    if (shouldRefreshToken(token)) {
      console.log('Token needs refresh, attempting refresh');
      const refreshed = await refreshAccessToken();
      return refreshed ? localStorage.getItem('token') : token;
    }
    
    return token;
  };

  const value: AuthContextType = {
    user,
    token,
    refreshToken,
    login,
    logout,
    isAuthenticated: isAuthenticated(),
    hasPermission,
    refreshAccessToken,
    getValidToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};


